import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type PagedResult } from "../lib/api";
import { useResource } from "../lib/hooks";
import type { EventDetail } from "../lib/types";
import { useSession } from "./session";

/**
 * The event the user is currently working inside.
 *
 * Almost every screen below the portfolio level is scoped to one event, and
 * people move between events constantly. Holding that choice here — and
 * remembering it across reloads — keeps the switch to one click instead of
 * re-navigating from the top each time.
 */

export interface EventOption {
  id: string;
  name: string;
  code: string;
  clientName: string | null;
  startDate: string | null;
  endDate: string | null;
  city: string | null;
  country: string | null;
  status: string;
  healthScore: number;
  healthBand: string;
  currency: string;
  bannerUrl: string | null;
}

/** An event's detail as a switcher option, for an event the loaded options do not hold. */
export function toEventOption(event: EventDetail): EventOption {
  return {
    id: event.id,
    name: event.name,
    code: event.code,
    clientName: event.clientName,
    startDate: event.startDate,
    endDate: event.endDate,
    city: event.city,
    country: event.country,
    status: event.status,
    healthScore: event.healthScore,
    healthBand: event.healthBand,
    currency: event.currency,
    bannerUrl: event.bannerUrl,
  };
}

interface EventContextValue {
  events: EventOption[];
  /** More events can be reached than `events`, the first page, holds; the switcher's search finds them. */
  hasMore: boolean;
  current: EventOption | null;
  currentId: string | null;
  isLoading: boolean;
  select: (eventId: string | null) => void;
}

/**
 * The remembered event is kept per person. One key for the whole browser handed the next person to sign in
 * whatever the previous one had open, so they started on an event that was not theirs.
 */
const LEGACY_STORAGE_KEY = "wolfray.event";
const storageKey = (userId: string) => `${LEGACY_STORAGE_KEY}.${userId}`;

const EventCtx = createContext<EventContextValue | null>(null);

function readRemembered(userId: string | undefined): string | null {
  if (!userId) return null;

  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function remember(userId: string | undefined, eventId: string | null): void {
  if (!userId) return;

  try {
    if (eventId) {
      localStorage.setItem(storageKey(userId), eventId);
    } else {
      localStorage.removeItem(storageKey(userId));
    }

    // The shared key belonged to whoever used it last, so it is not carried over to anyone.
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Remembering the choice is a convenience, not a requirement.
  }
}

/**
 * The event a session opens on when nothing is remembered for this person: their own team's event that is on
 * now or comes next, then their most recent one; failing those, the reachable event that is on now or comes
 * next; and only then the latest in the list.
 */
function mostRelevant(mine: EventOption[], events: EventOption[], today: string): EventOption | null {
  const notOver = (event: EventOption) => {
    const lastDay = event.endDate ?? event.startDate;
    return lastDay === null || lastDay >= today;
  };
  const byStart = (a: EventOption, b: EventOption) =>
    (a.startDate ?? "9999-12-31").localeCompare(b.startDate ?? "9999-12-31");

  const ownNext = mine.filter(notOver).sort(byStart)[0];
  if (ownNext) return ownNext;

  const own = [...mine].sort(byStart);
  if (own.length > 0) return own[own.length - 1];

  return events.filter(notOver).sort(byStart)[0] ?? events[0] ?? null;
}

export function EventContextProvider({ children }: { children: ReactNode }) {
  const { status, user } = useSession();
  const userId = user?.id;

  // What this person chose in this session. A choice made by someone else before a sign-out does not count.
  const [chosen, setChosen] = useState<{ userId: string; eventId: string | null } | null>(null);
  const remembered = useMemo(() => readRemembered(userId), [userId]);
  const currentId = chosen && chosen.userId === userId ? chosen.eventId : remembered;

  const enabled = status === "signedIn" && !user?.isExternal;

  // Keyed by the person, so the list a previous session loaded is never shown to the next one.
  const { data, isLoading } = useQuery({
    queryKey: ["event-options", userId],
    enabled,
    queryFn: () =>
      api.get<PagedResult<EventOption>>("/events", {
        page: 1,
        pageSize: 100,
        sortBy: "startDate",
        sortDescending: true,
      }),
  });

  const events = useMemo(() => data?.items ?? [], [data]);

  // The switcher lists this first page only. Events beyond it are found through the switcher's search.
  const hasMore = (data?.totalCount ?? 0) > events.length;

  // The events this person works on, so a first session opens on one of theirs rather than on
  // whichever event happens to start latest.
  const { data: mine, isLoading: mineLoading } = useQuery({
    queryKey: ["event-options", userId, "mine"],
    enabled,
    queryFn: () =>
      api.get<PagedResult<EventOption>>("/events", {
        page: 1,
        pageSize: 25,
        mineOnly: true,
        sortBy: "startDate",
      }),
  });

  const mineItems = useMemo(() => mine?.items ?? [], [mine]);

  const select = useCallback(
    (eventId: string | null) => {
      if (!userId) return;

      setChosen({ userId, eventId });
      remember(userId, eventId);
    },
    [userId],
  );

  // A remembered event the user can no longer reach — access revoked, event
  // archived — must not leave the shell pointing at nothing. Resolved during
  // render rather than corrected afterwards in an effect, so no pass of the UI
  // is ever drawn against an event that is not there.
  const today = new Date().toLocaleDateString("en-CA");

  const loaded = events.find((e) => e.id === currentId) ?? mineItems.find((e) => e.id === currentId);

  // An event chosen through the switcher's search, or remembered from such a choice, can lie beyond the first
  // page. It is read on its own so the choice holds. One this person can no longer reach, or that has been
  // archived, still gives way to the fallback below, as it did when only the list decided.
  const lookupId = enabled && currentId && !loaded && !isLoading && !mineLoading ? currentId : null;
  const lookup = useResource<EventDetail>(["event", lookupId ?? ""], `/events/${lookupId ?? ""}`, undefined, {
    enabled: lookupId !== null,
  });
  const lookupData = lookup.data;
  const lookedUp = useMemo(
    () =>
      lookupId && lookupData?.id === lookupId && lookupData.status !== "Archived"
        ? toEventOption(lookupData)
        : null,
    [lookupId, lookupData],
  );
  const lookingUp = lookupId !== null && !lookup.isError && lookupData?.id !== lookupId;

  // A remembered event wins while it can still be reached. Without one, the session opens on the event most
  // relevant to this person. Nothing is chosen while the options or their own events are loading, or while a
  // remembered event is being read, so a fallback is never remembered in place of the right answer.
  const current =
    loaded ??
    lookedUp ??
    (isLoading || mineLoading || lookingUp ? null : mostRelevant(mineItems, events, today));

  // The correction is only persisted. It deliberately does not call select():
  // the render above has already resolved the right event, and setting state
  // here would schedule a second pass to reach a value we already have.
  useEffect(() => {
    if (!current || current.id === currentId) return;

    remember(userId, current.id);
  }, [current, currentId, userId]);

  const value = useMemo<EventContextValue>(
    () => ({
      events,
      hasMore,
      current,
      currentId: current?.id ?? null,
      isLoading: isLoading || mineLoading || lookingUp,
      select,
    }),
    [events, hasMore, current, isLoading, mineLoading, lookingUp, select],
  );

  return <EventCtx.Provider value={value}>{children}</EventCtx.Provider>;
}

/**
 * Puts one event in context for everything inside it, whatever the shell has selected.
 *
 * An event workspace names its event in the address, so the module screens inside it work on that event
 * from their first render instead of on the previous selection until an effect catches up. `fallback`
 * supplies the event when it is not among the options (an archived event, say), and `onSelect` decides
 * what choosing another event means inside the workspace.
 */
export function ScopedEventProvider({
  eventId,
  fallback,
  onSelect,
  children,
}: {
  eventId: string;
  fallback: EventOption | null;
  onSelect: (eventId: string | null) => void;
  children: ReactNode;
}) {
  const outer = useEventContext();

  const value = useMemo<EventContextValue>(
    () => ({
      events: outer.events,
      hasMore: outer.hasMore,
      current:
        outer.events.find((e) => e.id === eventId) ?? (fallback?.id === eventId ? fallback : null),
      currentId: eventId,
      isLoading: outer.isLoading,
      select: onSelect,
    }),
    [outer.events, outer.hasMore, outer.isLoading, eventId, fallback, onSelect],
  );

  return <EventCtx.Provider value={value}>{children}</EventCtx.Provider>;
}

export function useEventContext(): EventContextValue {
  const context = useContext(EventCtx);

  if (!context) {
    throw new Error("useEventContext must be used inside an EventContextProvider.");
  }

  return context;
}

/**
 * The current event id for screens that require one. Returns null when no event
 * is selected, so screens can show a clear prompt rather than fetching with an
 * empty id and rendering an error.
 */
export function useCurrentEventId(): string | null {
  return useEventContext().currentId;
}
